<template>
  <teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-header">
          <button
            type="button"
            class="modal-close-button"
            @click="close"
            aria-label="Close upload modal"
          >
            &times;
          </button>
          <h3>{{ modalTitle }}</h3>
        </div>
        <div class="modal-body">
          <p class="instructions">
            {{ modalDescription }}
          </p>
          <div class="upload-area">
            <CompactUploader
                v-if="showLogoUploader"
                fileType="logo"
                label="Upload Logo"
                icon="📷"
                @upload-complete="onUploadComplete($event, 'logo')"
                @remove="onRemove('logo')"
                @validation-error="onValidationError('logo', $event)"
            />
            <CompactUploader
                v-if="showMapMarkerUploader"
                fileType="map-marker"
                label="Upload Map Marker"
                icon="📍"
                @upload-complete="onUploadComplete($event, 'mapMarker')"
                @remove="onRemove('mapMarker')"
                @validation-error="onValidationError('mapMarker', $event)"
            />
            <CompactUploader
                v-if="showArUploader"
                fileType="ar-asset"
                label="Upload 3D Object"
                icon="🧊"
                @upload-complete="onUploadComplete($event, 'ar')"
                @remove="onRemove('ar')"
                @validation-error="onValidationError('ar', $event)"
            />
            <CompactUploader
                v-if="showBackgroundUploader"
                fileType="background"
                label="Upload Background"
                icon="🖼️"
                @upload-complete="onUploadComplete($event, 'background')"
                @remove="onRemove('background')"
                @validation-error="onValidationError('background', $event)"
            />
            <p v-if="!hasUploaders" class="no-upload-message">
              Uploading isn't available for this asset type. Please browse default assets instead.
            </p>
          </div>
        </div>
        <div class="modal-footer">
            <p v-if="blockingError" class="blocking-error-notice">
              Fix the file error above before continuing.
            </p>
            <button
              @click="close"
              class="done-button"
              :disabled="Boolean(blockingError)"
            >Done</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue';
import type { PropType } from 'vue';
import CompactUploader from './CompactUploader.vue';

type SlotKey = 'logo' | 'mapMarker' | 'ar' | 'background';

export default defineComponent({
  name: 'UploadModal',
  components: {
    CompactUploader,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    allowedTypes: {
      type: Array as PropType<Array<'logo' | 'mapMarker' | 'ar' | 'background'>>,
      default: () => ['logo', 'mapMarker', 'ar', 'background'],
    },
  },
  emits: ['close', 'upload-complete', 'remove'],
  setup(props, { emit }) {
    const normalizedAllowedTypes = computed(() => {
      if (!props.allowedTypes || props.allowedTypes.length === 0) {
        return ['logo', 'mapMarker', 'ar', 'background'] as Array<'logo' | 'mapMarker' | 'ar' | 'background'>;
      }

      return props.allowedTypes.filter(
        (type): type is 'logo' | 'mapMarker' | 'ar' | 'background' =>
          type === 'logo' || type === 'mapMarker' || type === 'ar' || type === 'background'
      );
    });

    const showLogoUploader = computed(() => normalizedAllowedTypes.value.includes('logo'));
    const showMapMarkerUploader = computed(() => normalizedAllowedTypes.value.includes('mapMarker'));
    const showArUploader = computed(() => normalizedAllowedTypes.value.includes('ar'));
    const showBackgroundUploader = computed(() => normalizedAllowedTypes.value.includes('background'));

    const modalTitle = computed(() => {
      if (showLogoUploader.value && showMapMarkerUploader.value && showArUploader.value && showBackgroundUploader.value) {
        return 'Upload Your Assets';
      }
      if (showBackgroundUploader.value && !showLogoUploader.value && !showMapMarkerUploader.value && !showArUploader.value) {
        return 'Upload Your Background Image';
      }
      if (showArUploader.value && !showLogoUploader.value && !showMapMarkerUploader.value) {
        return 'Upload Your 3D Object';
      }
      if (showLogoUploader.value) {
        return 'Add Your Logo Here';
      }
      if (showMapMarkerUploader.value) {
        return 'Upload Your Map Marker';
      }
      return 'Upload Your Assets';
    });

    const modalDescription = computed(() => {
      if (showLogoUploader.value && showMapMarkerUploader.value && showArUploader.value && showBackgroundUploader.value) {
        return 'Upload your brand logo, custom map marker, 3D model, and a background image. Please ensure your files meet our guidelines for the best results.';
      }
      if (showBackgroundUploader.value && !showLogoUploader.value && !showMapMarkerUploader.value && !showArUploader.value) {
        return 'Upload a background image for the registration screen. Low-resolution images may appear blurry when stretched full screen.';
      }
      if (showArUploader.value && !showLogoUploader.value && !showMapMarkerUploader.value) {
        return 'Upload your 3D model (.glb). Please ensure your file meets our guidelines for the best results.';
      }
      if (showLogoUploader.value) {
        return 'Upload your logo or drop it below';
      }
      if (showMapMarkerUploader.value) {
        return 'Upload your custom map marker. Please ensure your file meets our guidelines for the best results.';
      }
      return 'No upload options are currently available for this asset type.';
    });

    const hasUploaders = computed(() => showLogoUploader.value || showMapMarkerUploader.value || showArUploader.value || showBackgroundUploader.value);

    // Track per-slot validation errors bubbled up from each CompactUploader.
    // Done stays disabled while any slot has a rejected file in it so the
    // user can't silently dismiss an oversize / wrong-type upload.
    const slotErrors = ref<Record<SlotKey, string | null>>({
      logo: null,
      mapMarker: null,
      ar: null,
      background: null,
    });
    const blockingError = computed<string | null>(() => {
      for (const key of Object.keys(slotErrors.value) as SlotKey[]) {
        const err = slotErrors.value[key];
        if (err) return err;
      }
      return null;
    });
    const onValidationError = (type: SlotKey, err: string | null) => {
      slotErrors.value[type] = err;
    };
    // Clear errors whenever the modal closes/re-opens so a prior rejected
    // state doesn't leak into the next session.
    watch(() => props.show, (isOpen) => {
      if (!isOpen) {
        slotErrors.value = { logo: null, mapMarker: null, ar: null, background: null };
      }
    });

    const close = () => {
      if (blockingError.value) return;
      emit('close');
    };

    const onUploadComplete = (payload: { file: File, staged: boolean, localPreview?: string }, type: SlotKey) => {
      emit('upload-complete', payload, type);
    };

    const onRemove = (type: SlotKey) => {
      slotErrors.value[type] = null;
      emit('remove', type);
    };

    return {
      close,
      onUploadComplete,
      onRemove,
      onValidationError,
      blockingError,
      showLogoUploader,
      showMapMarkerUploader,
      showArUploader,
      showBackgroundUploader,
      modalTitle,
      modalDescription,
      hasUploaders,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  z-index: calc(var(--base-layout-modal-z-index) + 90);
}

.modal-container,
.modal-header,
.modal-body,
.modal-footer {
  background: var(--ui-inverse-surface) !important;
}

.modal-container {
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
  --ui-inverse-text-muted: rgba(255, 255, 255, 0.84);
  --ui-inverse-cta-bg: var(--vt-c-accent);
  --ui-inverse-cta-bg-hover: var(--ui-inverse-cta-bg-hover);
  --ui-inverse-cta-text: var(--color-background);
}

.modal-header,
.modal-footer {
  border: none !important;
}

.modal-container h3,
.modal-container p,
.modal-container label,
.modal-container span {
  color: #ffffff !important;
}

.modal-overlay .modal-container h3 {
  color: #ffffff !important;
}

.upload-area :deep(.drop-zone) {
  border: 2px dashed rgba(255, 255, 255, 0.6) !important;
  background: rgba(15, 23, 42, 0.24);
}

.upload-area :deep(.drop-zone.is-dragging),
.upload-area :deep(.drop-zone:hover) {
  border-color: #ffffff !important;
  background: rgba(15, 23, 42, 0.38) !important;
}

.upload-area :deep(.prompt),
.upload-area :deep(.icon),
.upload-area :deep(.progress-indicator),
.upload-area :deep(.file-icon),
.upload-area :deep(.pending-notice),
.upload-area :deep(.validation-error),
.upload-area :deep(.resolution-warning) {
  color: #ffffff !important;
}

.instructions {
  color: #ffffff;
  opacity: 0.9;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 25px;
  text-align: center;
}

.upload-area {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  justify-items: center;
  justify-content: center;
}

.no-upload-message {
  grid-column: 1 / -1;
  text-align: center;
  color: #ffffff;
  font-size: 0.95rem;
}

.done-button {
    padding: 12px 24px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid var(--vt-c-accent) !important;
    background-color: var(--vt-c-accent) !important;
    color: var(--color-background) !important;
    width: 100%;
}

.done-button:hover {
    background-color: var(--ui-inverse-cta-bg-hover) !important;
    border-color: var(--ui-inverse-cta-bg-hover) !important;
    color: var(--color-background) !important;
}

.done-button:disabled,
.done-button:disabled:hover {
    opacity: 0.55;
    cursor: not-allowed;
    background-color: #555 !important;
    border-color: #555 !important;
    color: #ddd !important;
}

.blocking-error-notice {
    color: #ff9b9b;
    font-size: 0.9rem;
    text-align: center;
    margin-bottom: 10px;
}
</style>
